/*************************
 * Author : Matthieu ROBIN
 *************************/

import React, { FunctionComponent } from "react";

import {
  Card,
  CardMedia,
  CardActionArea,
  CardContent,
  Typography,
  Chip,
  CardHeader,
  Box,
  IconButton,
  Stack,
  Button,
  Menu,
  ListItemIcon,
  ListItemText,
  MenuItem,
  ListItem,
} from "@mui/material";
import { useNavigate } from "react-router";
import { Car, CarPosition, CarStatus } from "../models/car";
import {
  EmojiEvents,
  Factory,
  MoreVert,
  Search,
  SportsMotorsports,
  WorkspacePremium,
} from "@mui/icons-material";
import placeholder from "../images/car_placeholder.png";
import ebayLogo from "../images/ebay2.svg";
import lbcLogo from "../images/leboncoin.png";
import googleLogo from "../images/google.png";
import vintedLogo from "../images/vinted.png";

type CarCardProps = {
  car: Car;
};

const getChipColor = (status: CarStatus) => {
  switch (status) {
    case "Nouveau":
      return "#4DD091";
    case "Notable":
      return "#5f6f52";
    case "Souhaité":
      return "#B980FF";
    case "Réservé":
      return "#E6C84F";
    case "A garder":
      return "#00A5E3";
    case "A vendre":
      return "#FFA23A";
    case "En vente":
      return "#FF6F68";
    case "Vendu":
      return "#74737A";
  }
};

const getPositionIcon = (position: CarPosition) => {
  switch (position) {
    case "1st":
      return <EmojiEvents htmlColor="#FFD700" />;
    case "2nd":
      return <EmojiEvents htmlColor="#BCC6CC" />;
    case "3rd":
      return <EmojiEvents htmlColor="#CD7F32" />;
  }
};

const CarCard: FunctionComponent<CarCardProps> = ({ car }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();

  const goToCar = (id: string | undefined) => {
    const searchParams = new URLSearchParams(window.location.search);
    const queryParams = searchParams.toString()
      ? `?${searchParams.toString()}`
      : "";

    navigate(`/cars/${id}${queryParams}`);
  };

  const logoName = car.brand.toLocaleLowerCase().replace(" ", "-");

  const title = [
    car.brand,
    car.type,
    car.number ? `#${car.number}` : undefined,
  ].join(" ");

  const subheader = [car.competition, car.year].join(" ");

  const searchParams = [
    car.manufacturer,
    car.brand,
    car.type,
    car.number,
    car.competition,
    car.year,
  ];
  const searchQuery = searchParams
    .filter((param) => param !== undefined)
    .join("+");

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Card>
      <CardHeader
        avatar={
          <img
            src={`/logos/${logoName}.svg`}
            height={30}
            width={50}
            onError={({ currentTarget }) => {
              currentTarget.onerror = null;
              currentTarget.src = "/logos/unknown.svg";
            }}
          />
        }
        title={title}
        subheader={subheader}
        action={
          <Stack direction="row" alignItems="center">
            {car.category && (
              <Chip
                label={car.category}
                variant="outlined"
                size="small"
                icon={car.categoryWinner && <WorkspacePremium />}
                color="secondary"
                style={{
                  borderRadius: 5,
                  marginRight: 5,
                }}
              />
            )}
            {getPositionIcon(car.position)}
          </Stack>
        }
      />
      <CardActionArea onClick={() => goToCar(car._id)}>
        <CardMedia
          component="img"
          image={car.thumbnail || placeholder}
          height={200}
          alt="Voiture"
          decoding="async"
          loading="lazy"
        />
      </CardActionArea>
      <CardContent>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1}>
            <SportsMotorsports />
            {car.pilots.map((pilot, idx) => (
              <Chip
                label={pilot}
                key={`${idx}_${pilot}`}
                variant="outlined"
                size="small"
              />
            ))}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Factory />
            <Typography>{car.manufacturer} </Typography>
            <Typography variant="caption">{car.reference}</Typography>
          </Stack>
          <Box>
            <Chip
              label={car.status}
              style={{
                borderRadius: 5,
                backgroundColor: getChipColor(car.status),
                color: "white",
              }}
              size="small"
            />
            {(car.status === "Souhaité" || car.status === "Notable") && (
              <IconButton
                size="small"
                style={{ marginLeft: 2 }}
                onClick={handleOpen}
              >
                <Search fontSize="small" />
              </IconButton>
            )}
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <MenuItem
                href={`https://www.ebay.fr/sch/i.html?_nkw=${searchQuery}&_sacat=220&LH_PrefLoc=2`}
                component="a"
                target="_blank"
                rel="noopener noreferrer"
              >
                <ListItemIcon>
                  <img src={ebayLogo} height={14} />
                </ListItemIcon>
                <ListItemText>Ebay</ListItemText>
              </MenuItem>

              <MenuItem
                href={`https://www.leboncoin.fr/recherche?text=${searchQuery}`}
                component="a"
                target="_blank"
                rel="noopener noreferrer"
              >
                <ListItemIcon>
                  <img src={lbcLogo} height={14} />
                </ListItemIcon>
                <ListItemText>Leboncoin</ListItemText>
              </MenuItem>

              <MenuItem
                href={`https://www.vinted.fr/catalog?search_text=${searchQuery}`}
                component="a"
                target="_blank"
                rel="noopener noreferrer"
              >
                <ListItemIcon>
                  <img src={vintedLogo} height={14} />
                </ListItemIcon>
                <ListItemText>Vinted</ListItemText>
              </MenuItem>

              <MenuItem
                href={`https://www.google.fr/search?q=${searchQuery}`}
                component="a"
                target="_blank"
                rel="noopener noreferrer"
              >
                <ListItemIcon>
                  <img src={googleLogo} height={14} />
                </ListItemIcon>
                <ListItemText>Google</ListItemText>
              </MenuItem>

              <MenuItem
                href={`https://www.ebay.fr/sch/i.html?_nkw=${searchQuery}&_sacat=220&LH_Complete=1&LH_PrefLoc=2&LH_Sold=1`}
                component="a"
                target="_blank"
                rel="noopener noreferrer"
              >
                <ListItemIcon>
                  <img src={ebayLogo} height={14} style={{ opacity: 0.6 }} />
                </ListItemIcon>
                <ListItemText>Vendus sur ebay</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default CarCard;
